import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { homeFor, useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/seo";

export const AuthFrame = ({ title, subtitle, children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="flex flex-col justify-center px-6 py-12 sm:px-16">
      <Logo />
      <div className="rise mt-12 w-full max-w-md">
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
    <div className="relative hidden overflow-hidden bg-slate-950 lg:block">
      <img src="/images/hero.jpg" alt="Team at work" className="h-full w-full object-cover opacity-60" />
      <div className="absolute bottom-12 left-12 right-12 text-white">
        <p className="font-display text-3xl font-bold leading-tight">Every shift covered. Even the overnight ones.</p>
      </div>
    </div>
  </div>
);

export default function Login() {
  usePageMeta("login");
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(homeFor(user), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      nav(homeFor(data));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title="Welcome back" subtitle="Sign in to your rota.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </div>
        <Button type="submit" className="h-11 w-full rounded-full" disabled={busy} data-testid="login-submit-btn">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          New business? <Link to="/signup" className="font-semibold text-indigo-600" data-testid="goto-signup-link">Create a free account</Link>
        </p>
      </form>
    </AuthFrame>
  );
}
